// TUT-584 — os filtros da lista de Propostas, no vocabulário da tela: as listas
// fechadas de status e de modalidade (as opções do painel e a conferência da
// URL), o rótulo de cada uma e o recorte que viaja da tela ao servidor.
//
// O recorte vale para a tela INTEIRA: a lista, os quatro contadores, o quadro e
// o pop-up de cada contador leem o MESMO `RecorteDePropostas`, e é por isso que
// o número do cartão e o total da lista não discordam.
import type { ProposalListQuery } from "./api/infra";
import { periodoInvertido } from "./periodo-de-datas";
import { rotuloDaModalidade, situacaoDaProposta } from "./propostas-quadro";
import type { ProposalStatus, ProposalSummary } from "./types";
import type { CampoDoPeriodoDePropostas } from "../rotas/buscas";

export const STATUS_DE_PROPOSTA = [
  "DRAFT",
  "ISSUED",
  "SENT",
  "ACCEPTED",
  "DECLINED",
  "EXPIRED",
  "CANCELLED",
] as const satisfies readonly ProposalStatus[];

/** A modalidade como a coluna a mostra: a da Assistência, ou o tipo de serviço nas outras duas. */
export const MODALIDADES_DE_PROPOSTA = ["CONTRACT", "SINGLE_EXPERTISE", "CONSULTING", "SPECIAL"] as const;
export type ModalidadeDeProposta = (typeof MODALIDADES_DE_PROPOSTA)[number];

export function isStatusDeProposta(valor: string): valor is ProposalStatus {
  return (STATUS_DE_PROPOSTA as readonly string[]).includes(valor);
}

export function isModalidadeDeProposta(valor: string): valor is ModalidadeDeProposta {
  return (MODALIDADES_DE_PROPOSTA as readonly string[]).includes(valor);
}

export function rotuloDoStatusDeProposta(status: ProposalStatus): string {
  return situacaoDaProposta(status).label;
}

export function rotuloDaModalidadeDeProposta(modalidade: ModalidadeDeProposta): string {
  return rotuloDaModalidade(
    modalidade === "CONTRACT" || modalidade === "SINGLE_EXPERTISE"
      ? { serviceType: "ASSISTANCE", assistanceModality: modalidade }
      : { serviceType: modalidade, assistanceModality: null },
  );
}

/** A modalidade que a lista mostra para a proposta (o que o filtro compara). */
export function modalidadeDaProposta(
  proposta: Pick<ProposalSummary, "serviceType" | "assistanceModality">,
): string {
  return proposta.assistanceModality ?? proposta.serviceType;
}

/** O recorte da tela: o que o servidor recebe e o que as chaves da URL dizem. */
export interface RecorteDePropostas {
  q?: string;
  status?: ProposalStatus[];
  modalidade?: ModalidadeDeProposta[];
  cliente?: string[];
  campo?: CampoDoPeriodoDePropostas;
  de?: string;
  ate?: string;
}

/**
 * O recorte como o servidor o entende. O período de "criada em" / "enviada em"
 * vira o par `createdFrom/To` ou `sentFrom/To`; listas vazias não vão.
 */
export function consultaDoRecorte(
  recorte: RecorteDePropostas,
): Omit<ProposalListQuery, "page" | "pageSize" | "sort" | "dir"> {
  const [de, ate] =
    recorte.campo === "enviada"
      ? (["sentFrom", "sentTo"] as const)
      : (["createdFrom", "createdTo"] as const);
  return {
    ...(recorte.q ? { search: recorte.q } : {}),
    ...(recorte.status?.length ? { status: recorte.status } : {}),
    ...(recorte.modalidade?.length ? { modalidade: recorte.modalidade } : {}),
    ...(recorte.cliente?.length ? { cliente: recorte.cliente } : {}),
    ...(recorte.de ? { [de]: recorte.de } : {}),
    ...(recorte.ate ? { [ate]: recorte.ate } : {}),
  };
}

/** Há algum filtro ligado (o contador "de N" e a nota dos contadores só aparecem com recorte). */
export function recorteFiltrado(recorte: RecorteDePropostas): boolean {
  return Boolean(
    recorte.q ||
      recorte.status?.length ||
      recorte.modalidade?.length ||
      recorte.cliente?.length ||
      recorte.de ||
      recorte.ate,
  );
}

/**
 * O recorte que PODE ir ao servidor: período invertido ("de" depois de "até") a
 * API recusa com 400, e o 400 trocaria a tela inteira por um aviso de erro. A
 * tela avisa no painel e no quadro, e a consulta sai sem o período.
 */
export function recorteSemPeriodoInvertido(recorte: RecorteDePropostas): RecorteDePropostas {
  return periodoInvertido({ de: recorte.de ?? "", ate: recorte.ate ?? "" })
    ? { ...recorte, de: undefined, ate: undefined }
    : recorte;
}
